<template>
  <div v-html-safe="markdown" class="vc-markdown-render" />
</template>

<script setup lang="ts">
import { marked } from "marked";
import { computed } from "vue";
import DOMPurify from "dompurify";

interface IProps {
  src: string;
  // Innovadis custom prop includes
  imagesOnly?: boolean;
}

const props = defineProps<IProps>();

const markdown = computed(() => {
  const html = DOMPurify.sanitize(marked(props.src) as string, { USE_PROFILES: { html: true } });

  if (props.imagesOnly) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return Array.from(doc.querySelectorAll("img"))
      .map((img) => img.outerHTML)
      .join("");
  }

  return html;
});
</script>

<style lang="scss">
.vc-markdown-render {
  --radius: var(--vc-markdown-render-radius, var(--vc-radius, 0.5rem));

  font-size: 1rem;
  line-height: 1.5;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p,
  img,
  blockquote,
  pre,
  table,
  ul,
  ol,
  dl,
  hr {
    &:not(:last-child) {
      margin-bottom: 15px;
    }
  }

  li,
  dt,
  dd {
    &:not(:last-child) {
      margin-bottom: 0.25em;
    }

    & > ul,
    & > ol,
    & > dl {
      margin-top: 0.25em;
    }
  }

  h1 {
    font-size: 2rem;
    font-weight: 500;
    line-height: 2.75rem;
    letter-spacing: 0.02em;

    @media (screen(lg)) {
      font-size: 2.25rem;
      line-height: 2.875rem;
    }
  }

  h2 {
    font-size: 1.3rem;
    font-weight: 500;
    line-height: 1.75rem;
    letter-spacing: 0.02em;

    @media (screen(lg)) {
      font-size: 1.3rem;
      line-height: 1.75rem;
    }
  }

  h3 {
    font-size: 1.5rem;
    font-weight: 500;
    line-height: 2rem;
    letter-spacing: 0.02em;

    @media (screen(lg)) {
      font-size: 1.625rem;
      line-height: 2.375rem;
    }
  }

  h4 {
    font-size: 1.375rem;
    font-weight: 500;
    line-height: 1.675rem;
    letter-spacing: 0.02em;

    @media (screen(lg)) {
      font-size: 1.5rem;
      line-height: 2rem;
    }
  }

  h5 {
    font-size: 1.25rem;
    font-weight: 500;
    line-height: 1.75rem;
    letter-spacing: 0.02em;
  }

  h6 {
    font-weight: 500;
    letter-spacing: 0.02em;
  }

  a[href] {
    color: var(--link-color);

    &:hover {
      color: var(--link-hover-color);
    }
  }

  ul {
    margin-left: 20px;
    list-style-type: disc;

    ul {
      list-style-type: circle;
    }
  }

  ol {
    margin-left: 16px;
    list-style-type: decimal;

    li {
      padding-left: 4px;
    }
  }

  dt {
    font-weight: 500;
  }

  dd {
    margin-left: 16px;
  }

  blockquote {
    padding: 12px 24px;
    border-left: 4px solid theme("colors.neutral.400");
    border-radius: 0 var(--radius) var(--radius) 0;
    background-color: theme("colors.neutral.100");
  }

  pre {
    overflow: auto;
    padding: 16px;
    font-size: 85%;
    line-height: 1.45;
    background-color: theme("colors.neutral.100");
    border-radius: var(--radius);
  }

  code {
    background-color: theme("colors.neutral.100");
    border-radius: var(--radius);
    font-family: monospace;
    padding: 0 3px;
  }

  img {
    display: block;
    max-width: 100%;
    border-radius: var(--radius);
    object-position: 50% 50%;
    object-fit: contain;
  }

  table {
    @apply table-fixed overflow-hidden rounded-[--radius] border-separate border border-spacing-0 w-full text-base;

    th {
      padding: 8px 12px;
      background: theme("colors.neutral.100");
      font-weight: 500;
      text-align: left;
    }

    tr {
      @apply even:bg-neutral-50;
    }

    td,
    th {
      @apply align-top px-2 py-3 border-t break-words;

      @media (screen(lg)) {
        @apply p-3;
      }
    }

    th {
      @apply font-bold text-start border-0;
    }
  }
}
</style>
